import type { Root } from 'react-dom/client'
import type { EditorAdapter } from './adapters/adapter'
import { createRoot } from 'react-dom/client'
import { DocumentAdapter } from './adapters/document'
import { ForumAdapter } from './adapters/forum'
import { GenericAdapter } from './adapters/generic'
import { IssueAdapter } from './adapters/issue'
import { WikiAdapter } from './adapters/wiki'
import editorCss from './components/editor.css?inline'
import { RedmineTiptap } from './components/RedmineTiptap'

type AdapterFactory = (meta: HTMLElement, fieldId: string) => EditorAdapter

const FIELD_ADAPTER_FACTORIES: Record<string, AdapterFactory> = {
  content_text: meta => new WikiAdapter(meta),
  issue_description: meta => new IssueAdapter(meta, 'issue-description', '#issue_description'),
  issue_notes: meta => new IssueAdapter(meta, 'issue-notes', '#issue_notes'),
  document_description: meta => new DocumentAdapter(meta),
  message_content: meta => new ForumAdapter(meta),
}

export class RedmineTiptapWc extends HTMLElement {
  static observedAttributes = ['field-id']

  #container: HTMLDivElement
  #root?: Root

  constructor() {
    super()
    const shadow = this.attachShadow({ mode: 'open' })

    const styleEl = document.createElement('style')
    styleEl.textContent = editorCss
    shadow.appendChild(styleEl)

    this.#container = document.createElement('div')
    shadow.appendChild(this.#container)
  }

  connectedCallback() {
    if (this.#root)
      return

    const fieldId = this.getAttribute('field-id') ?? ''
    const factory = FIELD_ADAPTER_FACTORIES[fieldId] ?? ((m, id) => new GenericAdapter(m, id))
    const adapter = factory(this, fieldId)

    const textarea = document.querySelector<HTMLTextAreaElement>(adapter.textareaSelector)
    if (!textarea)
      return

    // Move this element to sit directly before the textarea, then adopt the textarea as a child.
    // moveBefore() is a state-preserving move that calls connectedMoveCallback instead of
    // the disconnect+connect cycle, so connectedCallback is not re-entered.
    textarea.parentElement!.moveBefore(this, textarea)
    this.appendChild(textarea)

    this.#root = createRoot(this.#container)
    this.#root.render(<RedmineTiptap adapter={adapter} textarea={textarea} />)
  }

  connectedMoveCallback() {
    // Called by moveBefore() instead of disconnectedCallback+connectedCallback — nothing to do.
  }

  disconnectedCallback() {
    this.#root?.unmount()
    this.#root = undefined
  }
}

customElements.define('redmine-tiptap-editor', RedmineTiptapWc)

declare global {
  interface HTMLElementTagNameMap {
    'redmine-tiptap-editor': RedmineTiptapWc
  }
}
